/**
 * Google Tag Manager Component for React
 *
 * Initializes GTM with consent mode and user tracking.
 * Task 2.11.6 | Wave 2d Tier 1
 */

import { useEffect, useCallback } from 'react';

import { useAuth } from '../providers/AuthProvider';

/**
 * Consent state for GDPR/CCPA compliance
 */
interface ConsentState {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
}

interface GTMConfig {
  containerId: string;
  enableUserTracking?: boolean;
}

/**
 * Get GTM container ID based on environment
 */
function getContainerId(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'portal.geauxfile.com' || hostname === 'admin.geauxfile.com') {
    return import.meta.env.VITE_GTM_CONTAINER_ID ?? 'GTM-NKWWM3ML';
  }

  if (hostname.includes('staging') || hostname.includes('preview')) {
    return import.meta.env.VITE_GTM_CONTAINER_ID_STAGING ?? 'GTM-NKWWM3ML';
  }

  return import.meta.env.VITE_GTM_CONTAINER_ID_DEV ?? 'GTM-NKWWM3ML';
}

/**
 * Initialize consent mode defaults
 */
function initConsentDefaults(): void {
  window.dataLayer = window.dataLayer || [];

  function gtag(...args: unknown[]): void {
    window.dataLayer.push(args);
  }

  // Default consent state (denied until user grants)
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });

  // Region-specific defaults for EU/EEA
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB',
    ],
  });

  // US defaults (less restrictive — grant ad_storage for Google Ads conversion attribution)
  gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    analytics_storage: 'granted',
    region: ['US'],
  });

  // California (CCPA)
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
    region: ['US-CA'],
  });
}

/**
 * Get site-specific GA4 Measurement ID (defense-in-depth alongside GTM lookup)
 */
function getGA4MeasurementId(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'portal.geauxfile.com' || hostname === 'admin.geauxfile.com') {
    return 'G-7GH09SCQ3P';
  }
  return 'G-FN0MPMEYX6';
}

/**
 * Direct GA4 config — guarantees page views route to correct stream regardless of GTM
 */
function initDirectGA4(): void {
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]): void {
    window.dataLayer.push(args);
  }
  const measurementId = getGA4MeasurementId();
  gtag('js', new Date());
  gtag('config', measurementId, { send_page_view: true });
  gtag('config', 'AW-17836361643');
}

/**
 * Load GTM script
 */
function loadGTMScript(containerId: string): void {
  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`)) {
    return;
  }

  // Initialize dataLayer and start event
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Create and insert script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
}

/**
 * Load GTM noscript iframe
 */
function loadGTMNoScript(containerId: string): void {
  // Check if already loaded
  if (document.querySelector(`noscript iframe[src*="googletagmanager.com/ns.html?id=${containerId}"]`)) {
    return;
  }

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);

  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Push user properties to data layer
 */
function pushUserProperties(userId: string | undefined, customerId?: string, tenantId?: string): void {
  if (!userId) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_properties_update',
    userId,
    customerId,
    tenantId,
    userRole: 'customer',
  });
}

/**
 * GTM Provider Component
 */
export function GTM({ containerId, enableUserTracking = true }: Partial<GTMConfig>): null {
  const { user, isAuthenticated } = useAuth();

  const initGTM = useCallback(() => {
    const id = containerId ?? getContainerId();

    if (!id || id.startsWith('GTM-X')) {
      if (import.meta.env.DEV) {
        console.warn('[GTM] Container ID not configured');
      }
      return;
    }

    // Initialize consent defaults first
    initConsentDefaults();

    // Load GTM script
    loadGTMScript(id);

    // Defense-in-depth: direct GA4 config for correct stream routing
    initDirectGA4();

    // Load noscript fallback
    loadGTMNoScript(id);
  }, [containerId]);

  // Initialize GTM on mount
  useEffect(() => {
    initGTM();
  }, [initGTM]);

  // Push user properties when authenticated
  useEffect(() => {
    if (enableUserTracking && isAuthenticated && user?.userId) {
      pushUserProperties(user.userId);
    }
  }, [enableUserTracking, isAuthenticated, user]);

  return null;
}

/**
 * Update consent state (call when user updates preferences)
 */
export function updateConsent(consent: Partial<ConsentState>): void {
  window.dataLayer = window.dataLayer || [];

  function gtag(...args: unknown[]): void {
    window.dataLayer.push(args);
  }

  gtag('consent', 'update', consent);

  // Also push as event for GTM triggers
  window.dataLayer.push({
    event: 'consent_update',
    ...consent,
  });
}

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(pagePath?: string, pageTitle?: string): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: pagePath ?? window.location.pathname,
    page_title: pageTitle ?? document.title,
    page_location: window.location.href,
  });
}

export default GTM;
